import 'react-app-polyfill/ie11';
import 'react-app-polyfill/stable';
import * as React from 'react';
import * as downloadFile from 'downloadjs';
import { Theme, StyleRules } from '@material-ui/core/styles';
import withStyles, { ClassNameMap } from '@material-ui/core/styles/withStyles';
import { Search as SearchIcon, SaveAlt as SaveIcon } from '@material-ui/icons';
import {
  Button,
  CircularProgress,
  FormControl,
  Grid,
  Paper,
  TextField,
  InputLabel,
  Select,
  MenuItem,
} from '@material-ui/core';
import {
  defaultPerPage,
  SearchConditions,
  SearchResult as SearchType,
  statusPatterns,
  SearchContentsState,
} from '../modules/search';
import { DownloadConditions } from '../modules/searchTsv';
import {
  TagsResult as TagsType,
} from '../modules/tags';
import SnackbarsMessage from './SnackbarsMessage';
import SearchList from './SearchList';
import { goLoginPage } from 'src/modules/pages';
import './Search.css';

interface Props {
  classes: ClassNameMap;
  // 検索関連----------------------
  search: SearchType | null;
  searchState: SearchContentsState;
  searchMessages: string[];
  searchDocuments: (conditions: SearchConditions) => Promise<void>;
  clearSearchMessages: () => void;
  // ダウンロード関連----------------------
  searchDownload: Promise<Blob> | null;
  downloadMessages: string[];
  downloadDocuments: (conditions: DownloadConditions) => Promise<void>;
  clearDownloadMessages: () => void;
  // カテゴリ関連----------------------
  tags: TagsType | null;
  tagsMessages: string[];
  getTags: () => Promise<void>;
  newCategories: number[];  // containers/Searchで定義されている
  setUpdateCategory: (index: number, newTagId: number) => void;
  setUpdateCategories: (newCategories: number[]) => void;
  // 各Action関連----------------------
  updateContent: (index: number, id: string, newTagId: number, status: number) => Promise<void>;
  updateMessages: string[];
  deleteContent: (index: number, id: string) => Promise<void>;
  goLoginPage: () => void;
  tagStatus: number;
  searchStatus: number;
  updateStatus: number;
  tsvStatus: number;
  deleteStatus: number;
}

interface State {
  searchConditions: SearchConditions;
}

const style = (theme: Theme): StyleRules => ({
  root: {
    flexGrow: 1,
  },
  paper: {
    padding: theme.spacing.unit * 2,
    marginBottom: theme.spacing.unit * 5,
  },
  wrapper: {
    margin: theme.spacing.unit,
    position: 'relative',
  },
  formControl: {
    display: 'flex',
    flexWrap: 'wrap',
    'justify-content': 'center',
    margin: theme.spacing.unit,
  },
  searchButton: {
    margin: theme.spacing.unit * 3,
  },
  buttonProgress: {
    color: '#FFFFFF',
    position: 'absolute',
    top: '50%',
    left: '50%',
    marginTop: -12,
    marginLeft: -12,
  },
});

class Search extends React.Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = {
      searchConditions: {
        name: '',
        status: 0,
        newTagId: 0,
        orgTagId: 0,
        limit: defaultPerPage,
        offset: 0,
        page: 1,
      },
    };
    this.handleLoginPage = this.handleLoginPage.bind(this);
  }

  componentDidMount() {
    const { getTags } = this.props;
    getTags().then(() => {
      const { tagStatus } = this.props;
      if (tagStatus === 401 || tagStatus === 403) {
        document.cookie = 'access_token=; expires=0';
        document.cookie = 'username=; expires=0';
        this.handleLoginPage();
      }
    });
  }

  // --------------
  // 検索条件変更
  // --------------
  handleChangeSearchName = (event: React.ChangeEvent<HTMLSelectElement>) => {
    const { value } = event.target;
    const conditions = { ...this.state.searchConditions };
    conditions.name = value;
    this.setState({ searchConditions: conditions });
  }
  handleChangeSearchStatus = (event: React.ChangeEvent<HTMLSelectElement>) => {
    const { value } = event.target;
    const conditions = { ...this.state.searchConditions };
    conditions.status = +value;
    this.setState({ searchConditions: conditions });
  }
  handleChangeSearchNewTagId = (event: React.ChangeEvent<HTMLSelectElement>) => {
    const { value } = event.target;
    const conditions = { ...this.state.searchConditions };
    conditions.newTagId = +value;
    this.setState({ searchConditions: conditions });
  }
  handleChangeSearchOrgTagId = (event: React.ChangeEvent<HTMLSelectElement>) => {
    const { value } = event.target;
    const conditions = { ...this.state.searchConditions };
    conditions.orgTagId = +value;
    this.setState({ searchConditions: conditions });
  }

  handleChangePage = () => {
    this.onSearch();
  }

  handleSearch = () => {
    const conditions = { ...this.state.searchConditions };
    conditions.limit = defaultPerPage;
    conditions.offset = 0;
    this.setState({ searchConditions: conditions }, () => {
      this.onSearch();
    });
  }

  onSearch = () => {
    const { tags, searchDocuments, setUpdateCategories, goLoginPage } = this.props;
    // FIXME 画面が固まってる気がする
    searchDocuments(this.state.searchConditions).then(() => {
      const { search, searchStatus } = this.props;
      let newCategories = [];
      // tokenチェックエラーの場合、トークンを削除し、画面遷移を発生させ、ログインページに遷移
      if (searchStatus === 403 || searchStatus === 401) {
        document.cookie = 'access_token=; expires=0';
        document.cookie = 'username=; expires=0';
        goLoginPage();
      } else if (search && tags) {
        newCategories = Object.keys(search.errorSentences).map((key) => {
          return search.errorSentences[key]['newTagId'];
        });
        setUpdateCategories(newCategories);
      }
    });
  }

  handleDownloadTsv = () => {
    const { downloadDocuments } = this.props;
    downloadDocuments(this.state.searchConditions).then(() => {
      const { searchDownload, tsvStatus, goLoginPage } = this.props;
      if (tsvStatus === 403 || tsvStatus === 401) {
        document.cookie = 'access_token=; expires=0';
        document.cookie = 'username=; expires=0';
        goLoginPage();
      } else if (searchDownload) {
        searchDownload.then((blob) => {
          downloadFile(blob, '再学習データ.csv');
        });
      }
    });
  }

  // 検索結果1件毎に新カテゴリを設定
  handleSetNewTagId = (index: number, newTagId: number) => {
    this.props.setUpdateCategory(index, newTagId);
  }

  // 確定
  handleUpdateContent = (index: number, id: string, newTagId: number, status: number) => {
    return this.props.updateContent(index, id, newTagId, status).then(() => {
      const { updateStatus, goLoginPage } = this.props;
      if (updateStatus === 403 || updateStatus === 401) {
        document.cookie = 'access_token=; expires=0';
        document.cookie = 'username=; expires=0';
        goLoginPage();
      }
    });
  }

  // 削除
  handleDeleteContent = (index: number, id: string) => {
    return this.props.deleteContent(index, id).then(() => {
      const { deleteStatus, goLoginPage } = this.props;
      if (deleteStatus === 403 || deleteStatus === 401) {
        document.cookie = 'access_token=; expires=0';
        document.cookie = 'username=; expires=0';
        goLoginPage();
      }
    });
  }

  // ログインページへの遷移
  handleLoginPage = () => {
    goLoginPage();
  }

  render() {
    const {
      classes,
      search,
      searchStatus,
      searchMessages,
      searchState,
      downloadMessages,
      tagsMessages,
      tags,
      tagStatus,
      newCategories,
      updateMessages,
      updateStatus,
      tsvStatus,
      deleteStatus,
    } = this.props;
    const loading = searchState === 'searching';

    // tokenチェックエラーの場合、トークンを削除し、画面遷移を発生させ、ログインページに遷移
    if (
      tagStatus === 403 || tagStatus === 401 ||
      searchStatus === 403 || searchStatus === 401 ||
      updateStatus === 403 || updateStatus === 401 ||
      tsvStatus === 403 || tsvStatus === 401 ||
      deleteStatus === 403 || deleteStatus === 401) {
      document.cookie = 'access_token=; expires=0';
      document.cookie = 'username=; expires=0';
      this.handleLoginPage();
    }

    return (
      <React.Fragment>
        <SnackbarsMessage
          variant="error"
          messages={searchMessages}
        />
        <SnackbarsMessage
          variant="error"
          messages={downloadMessages}
        />
        <SnackbarsMessage
          variant="error"
          messages={tagsMessages}
        />
        <Paper className={classes.paper}>
          <Grid
            container
            justify="center"
          >
            <Grid item xs={6}>
              <FormControl className={classes.formControl}>
                <TextField
                  id="applier-name"
                  label="申請者"
                  type="text"
                  className={classes.textField}
                  value={this.state.searchConditions.name}
                  onChange={this.handleChangeSearchName}
                />
              </FormControl>
            </Grid>
            <Grid item xs={6}>
              <FormControl className={classes.formControl}>
                <InputLabel htmlFor="set-status">ステータス</InputLabel>
                <Select
                  id="set-status"
                  name="status"
                  value={this.state.searchConditions.status}
                  onChange={this.handleChangeSearchStatus}
                >
                  <MenuItem value="">
                    <em></em>
                  </MenuItem>
                  {Object.keys(statusPatterns).map((k, i) => (
                    <MenuItem key={i} value={k}>{statusPatterns[k]}</MenuItem >
                  ))}
                </Select>
              </FormControl>
            </Grid>
          </Grid>
          <Grid
            container
            justify="center"
          >
            <Grid item xs={6}>
              <FormControl className={classes.formControl}>
                <InputLabel htmlFor="set-new-category">新カテゴリ</InputLabel>
                <Select
                  id="set-new-category"
                  name="new-category"
                  value={this.state.searchConditions.newTagId}
                  onChange={this.handleChangeSearchNewTagId}
                >
                  <MenuItem value="">
                    <em></em>
                  </MenuItem>
                  {tags ? Object.keys(tags.tags).map(k => (
                    <MenuItem key={k} value={k}>{tags.tags[k]}</MenuItem>
                  )) : (null)}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={6}>
              <FormControl className={classes.formControl}>
                <InputLabel htmlFor="set-org-category">旧カテゴリ</InputLabel>
                <Select
                  id="set-org-category"
                  name="org-category"
                  value={this.state.searchConditions.orgTagId}
                  onChange={this.handleChangeSearchOrgTagId}
                >
                  <MenuItem value="">
                    <em></em>
                  </MenuItem>
                  {tags ? Object.keys(tags.tags).map(k => (
                    <MenuItem key={k} value={k}>{tags.tags[k]}</MenuItem>
                  )) : (null)}
                </Select>
              </FormControl>
            </Grid>
          </Grid>
          <Grid
            container
            justify="center"
          >
            <Grid item>
              <div className={classes.wrapper}>
                <Button
                  className={classes.searchButton}
                  variant="contained"
                  color="primary"
                  disabled={loading}
                  onClick={this.handleSearch}
                >
                  <SearchIcon />検索
                </Button>
                {loading &&
                  <CircularProgress
                    size={24}
                    className={classes.buttonProgress}
                  />}
              </div>
            </Grid>
            <Grid item>
              <div className={classes.wrapper}>
                <Button
                  className={classes.searchButton}
                  variant="contained"
                  color="primary"
                  disabled={loading}
                  onClick={this.handleDownloadTsv}
                >
                  <SaveIcon />TSVダウンロード
                </Button>
                {loading &&
                  <CircularProgress
                    size={24}
                    className={classes.buttonProgress}
                  />}
              </div>
            </Grid>
          </Grid>
        </Paper>
        {!loading && search && search.errorSentences.length > 0 ? (
          <SearchList
            search={search}
            onChangePage={this.handleChangePage}
            conditions={this.state.searchConditions}
            tags={tags}
            newCategories={newCategories}
            onChangeNewTagId={this.handleSetNewTagId}
            onUpdate={this.handleUpdateContent}
            updateMessages={updateMessages}
            onDelete={this.handleDeleteContent}
            onSearch={this.onSearch}
          />
        ) : ('検索結果: 0件')
        }
      </React.Fragment>
    );
  }
}

export default withStyles(style)(Search);



// WEBPACK FOOTER //
// ./src/components/Search.tsx